import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Tooltip} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import useDebounce from '../../../components/utlis/useDebounce';
import {companyIdSelector} from '../../../store/Slices/companySlice';
import {useStoreSelector} from '../../../store/configstore';
import {useRqMediaTracksArtistOrSong} from '../../../react_query/media_tracks/ListMediaTrackPaginatPro';
import FuzzyAutocomplete from './FuzzyAutocomplete';

export type IFuzzyFilters = {
  track_artist: string;
  track_name: string;
  fuzzy: boolean;
};

export type IFuzzyQueryFilters = {
  track_artist: string;
  track_name: string;
};

type Props = {
  trackArtist: string;
  trackName: string;
  onFuzzySearchChange: (filters: IFuzzyFilters) => void;
  onReset: () => void;
};

const FuzzySearchSong = ({
  trackName,
  trackArtist,
  onReset,
  onFuzzySearchChange,
}: Props) => {
  const [artistInput, setArtistInput] = useState(trackArtist);
  const [trackInput, setTrackInput] = useState(trackName);

  const companyId = useStoreSelector(companyIdSelector);

  const debouncedArtist = useDebounce(artistInput, 500);
  const debouncedTrack = useDebounce(trackInput, 500);

  const {data: artistData, isFetching: artistLoading} =
    useRqMediaTracksArtistOrSong(
      'track_artist',
      debouncedArtist ?? '',
      debouncedTrack ?? '',
      companyId,
      (debouncedArtist ?? '').length >= 3,
    );

  const {data: trackData, isFetching: trackLoading} =
    useRqMediaTracksArtistOrSong(
      'track_name',
      debouncedTrack ?? '',
      debouncedArtist ?? '',
      companyId,
      (debouncedTrack ?? '').length >= 3,
    );

  const isArtistTooShort =
    artistInput?.trim().length > 0 && artistInput?.trim().length < 3;
  const isTrackTooShort =
    trackInput?.trim().length > 0 && trackInput?.trim().length < 3;

  const artistOptions = useMemo(() => {
    if (!artistData?.data?.length || isArtistTooShort) return [];
    return [...new Set(artistData.data.map(item => item.name))];
  }, [artistData, isArtistTooShort]);

  const trackOptions = useMemo(() => {
    if (!trackData?.data?.length || isTrackTooShort) return [];
    return [...new Set(trackData.data.map(item => item.name))];
  }, [trackData, isTrackTooShort]);

  useEffect(() => {
    const validArtist = (debouncedArtist ?? '').length >= 3;
    const validTrack = (debouncedTrack ?? '').length >= 3;

    onFuzzySearchChange({
      track_artist: validArtist ? debouncedArtist ?? '' : '',
      track_name: validTrack ? debouncedTrack ?? '' : '',
      fuzzy: validArtist || validTrack,
    });
  }, [debouncedArtist, debouncedTrack, onFuzzySearchChange]);

  const handleReset = useCallback(() => {
    setArtistInput('');
    setTrackInput('');
    onReset();
  }, [onReset]);

  return (
    <Box
      className='w-full flex items-center justify-center'
      sx={{columnGap: '10px'}}>
      {/* Artist Autocomplete */}
      <FuzzyAutocomplete
        label='Search Artist'
        inputValue={artistInput}
        options={artistOptions}
        loading={artistLoading}
        isTooShort={isArtistTooShort}
        noOptionsText='No artist found'
        onInputChange={setArtistInput}
      />

      {/* Track Autocomplete */}
      <FuzzyAutocomplete
        label='Search Song'
        inputValue={trackInput}
        options={trackOptions}
        loading={trackLoading}
        isTooShort={isTrackTooShort}
        noOptionsText='No song found'
        onInputChange={setTrackInput}
      />
      {/* <ReplayIcon /> */}
      <Tooltip title='Reset' arrow describeChild>
        <span>
          <Button
            variant='outlined'
            color='primary'
            onClick={handleReset}
            aria-label='Reset button '
            sx={{
              minWidth: 'max-content',
              alignItems: 'center',
              display: 'flex',
              '& .MuiButton-startIcon': {
                margin: 0,
              },
            }}
            startIcon={
              <ReplayIcon
                sx={{
                  fontSize: 22,
                }}
              />
            }
            disabled={artistInput.length === 0 && trackInput.length === 0}
          />
        </span>
      </Tooltip>
    </Box>
  );
};

export default FuzzySearchSong;
